
import { useEffect } from "react";
import { useProfile } from "@/hooks/useProfile";
import { CompleteProfileModal } from "@/components/settings/profile/CompleteProfileModal";
import { useAuthenticationCheck } from "./hooks/useAuthenticationCheck";
import { useProfileValidation } from "./hooks/useProfileValidation";
import { useModalState } from "./hooks/useModalState";
import { useProfileCompletion } from "./hooks/useProfileCompletion";
import { clearProfileCache } from "@/utils/clearProfileCache";

export const GlobalProfileCheck = () => {
  const { profile, updateProfile, isLoading, refetch } = useProfile();
  const { isAuthenticated, shouldSkipProfileCheck, currentPath } = useAuthenticationCheck();
  const { isProfileIncomplete } = useProfileValidation();
  const {
    showModal,
    setShowModal,
    isProfileBeingCompleted,
    setIsProfileBeingCompleted,
    modalShownInSession,
    showProfileModal,
    hideProfileModal
  } = useModalState();

  // Clear cache on mount to ensure fresh data
  useEffect(() => {
    if (isAuthenticated) {
      clearProfileCache();
    }
  }, [isAuthenticated]);

  const { handleProfileComplete } = useProfileCompletion({
    updateProfile,
    refetch,
    onProfileComplete: () => {
      hideProfileModal();
    },
    onProfileError: () => {
      setIsProfileBeingCompleted(true);
      setShowModal(true);
    }
  });

  // Verificar si el perfil está incompleto
  useEffect(() => {
    // Solo realizar la verificación cuando el usuario está autenticado
    // y no estamos en rutas excluidas
    if (shouldSkipProfileCheck || !isAuthenticated) {
      console.log("Skipping profile check because:", {
        shouldSkipProfileCheck,
        path: currentPath,
        isAuthenticated,
        reason: shouldSkipProfileCheck ? "Route excluded from profile check" : "Not authenticated"
      });
      return;
    }

    // CRÍTICO: Solo evaluar si NO estamos cargando Y tenemos datos reales
    // Esto previene mostrar el modal durante recargas temporales
    if (isLoading || !profile) {
      console.log("Skipping profile validation - still loading or no profile data:", {
        isLoading,
        hasProfile: !!profile,
        currentPath
      });
      return;
    }
    
    const profileIsIncomplete = isProfileIncomplete(profile);
    
    console.log("Profile validation for route:", currentPath, {
      first_name: profile.first_name,
      last_name: profile.last_name,
      birthdate: profile.birthdate,
      isIncomplete: profileIsIncomplete,
      modalShownInSession,
      isProfileBeingCompleted,
      currentPath
    });
    
    // Verificación adicional: si todos los campos requeridos están presentes,
    // no mostrar el modal independientemente de otros estados
    const hasAllRequiredFields = !!(profile.first_name && profile.last_name && profile.birthdate);
    
    if (hasAllRequiredFields) {
      console.log("Profile has all required fields, hiding modal if shown");
      if (isProfileBeingCompleted || showModal) {
        hideProfileModal();
      }
      return;
    }
    
    // Solo mostrar modal si el perfil está realmente incompleto (no durante cargas temporales)
    if (profileIsIncomplete && !modalShownInSession && !isProfileBeingCompleted) {
      console.log("Profile is incomplete, showing forced modal on path:", currentPath);
      showProfileModal();
    } else if (!profileIsIncomplete && isProfileBeingCompleted) {
      hideProfileModal();
    }
  }, [
    profile, 
    isLoading, 
    currentPath, 
    isAuthenticated, 
    shouldSkipProfileCheck, 
    isProfileBeingCompleted,
    modalShownInSession,
    isProfileIncomplete,
    showProfileModal,
    hideProfileModal,
    showModal
  ]);

  // No mostrar nada si estamos cargando, no hay sesión, o estamos en rutas excluidas
  if (isLoading || !isAuthenticated || shouldSkipProfileCheck) {
    return null;
  }

  // Solo mostrar el modal si debe mostrarse y el perfil está incompleto
  const shouldShowModal = showModal && profile && isProfileIncomplete(profile);

  // Verificación final: no mostrar si todos los campos están completos
  const hasAllRequiredFields = !!(profile?.first_name && profile?.last_name && profile?.birthdate);
  if (hasAllRequiredFields) {
    return null;
  }

  if (!shouldShowModal) {
    return null;
  }

  return (
    <CompleteProfileModal 
      profile={profile} 
      onComplete={handleProfileComplete} 
      forceOpen={true}
    />
  );
};
